import { MultiSelect } from "../../forms/components/MultiSelect";
import { useForm } from "react-hook-form";
import { Key } from "../../shared/models/Key";
import { WingmanAdminDocument } from "../models/WingmanAdminDocument";
import { canEdit } from "../utils/helpers";
import { z } from "zod";
import { t } from "i18next";
import { useBrands } from "../../core/hooks/useBrands";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "../../forms/components/Form";
import { FormRow } from "../../forms/components/FormRow";

const WingmanAdminBrandSelect = ({
  brands: preSelectedBrands,
  handleSelectedBrands,
  filename,
  id,
  uploadStatus,
  authorizedBrands,
}: {
  brands: string[];
  handleSelectedBrands: (payload: WingmanAdminDocument) => void;
  filename: string;
  id: number;
  uploadStatus: string;
  authorizedBrands: string[];
}) => {
  const userCanEdit = canEdit(authorizedBrands, preSelectedBrands);
  const disabled = uploadStatus === "Complete" && !userCanEdit;
  const allBrands = useBrands();
  const userHasAllBrandsAuth = authorizedBrands.includes("UB");
  const filteredBrands = userHasAllBrandsAuth
    ? allBrands.filter((b) => b.id !== -1 && b.abbreviation !== "UB")
    : allBrands.filter(
      (b) =>
        b.id !== -1 &&
          b.abbreviation !== "UB" &&
          authorizedBrands.includes(b.abbreviation ?? "")
    );

  const schema = z.object({
    brands: z.array(z.number()).optional(),
  });
  type Schema = z.infer<typeof schema>;

  const defaultBrandIds = preSelectedBrands
    .map((abbr) => allBrands.find((b) => b.abbreviation === abbr)?.id)
    .filter((id) => id !== undefined) as number[];

  // If no preselected brands => show empty selection
  const defaultValues: Schema = {
    brands: preSelectedBrands.length === 0 ? [] : defaultBrandIds,
  };

  const methods = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const { register } = methods;

  const convertToAbbreviations = (ids: number[] | undefined): string[] => {
    if (!ids) return [];
    return ids
      .map((id) => allBrands.find((b) => b.id === id)?.abbreviation)
      .filter((abbr) => abbr !== undefined) as string[];
  };

  const selectedBrandsMatch = (arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((val, idx) => val === sorted2[idx]);
  };

  const handleBlurSubmit = () => {
    const brandIds = methods.getValues("brands") ?? [];
    if (!brandIds) return;

    // Check if user selected all brands
    const filteredBrandIds = filteredBrands.map((b) => b.id);
    const hasAllBrandsSelected =
      brandIds.length === filteredBrandIds.length &&
      brandIds.every((id) => filteredBrandIds.includes(id));

    // If they selected all => treat that as “All Brands” (i.e. pass everything)
    let newAbbreviations: string[];
    if (hasAllBrandsSelected) {
      // Add all brand abbreviations from the entire brand list
      newAbbreviations = userHasAllBrandsAuth
        ? allBrands.map((b) => b.abbreviation || "UB")
        : convertToAbbreviations(brandIds); // or just the authorized subset
    } else {
      newAbbreviations = convertToAbbreviations(brandIds);
    }

    // Only call handleSelectedBrands if different from preSelected
    if (!selectedBrandsMatch(preSelectedBrands, newAbbreviations)) {
      handleSelectedBrands({
        id,
        Filename: filename,
        Brand: newAbbreviations,
        uploadStatus,
      });
    }
  };

  const getBrandDisplayedText = (
    data: Key<string | number>[],
    value?: (string | number | undefined)[]
  ): string => {
    if (!value?.length) {
      return "";
    }

    if (value.length === data.length) {
      return t`All Brands`;
    }

    if (value.length === 1) {
      const single = data.find((d) => d.id === value[0]);
      return single?.name ?? "";
    }

    return t`Multiple Brands`;
  };

  const handleSubmit = () => {
    // NOOP (just required for the <Form> submission)
  };

  return (
    <Form {...methods} onSubmit={handleSubmit}>
      <FormRow>
        <MultiSelect
          label=""
          onBlurSubmit={handleBlurSubmit}
          {...register("brands")}
          displayText={getBrandDisplayedText}
          disabled={disabled}
          data={filteredBrands.map((b) => ({ id: b.id, name: b.name }))}
        />
      </FormRow>
    </Form>
  );
};

export default WingmanAdminBrandSelect;

